import { Button, Input, Modal } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ForwardToPlatform,
  Organization,
  OrganizationMutableProperties,
} from "../api/models";

interface OrganizationPropertyModalProps {
  modalButtonIcon?: React.ReactNode;
  title: string;
  organization?: Organization;
  onSave(organization: OrganizationMutableProperties): Promise<any>;
}

export function OrganizationPropertyModal(
  props: OrganizationPropertyModalProps
) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newName, setNewName] = useState<string>();
  const [newForward, setNewForward] = useState<ForwardToPlatform | undefined>();

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const effectiveName = newName ?? props.organization?.name;

  const onSave = async () => {
    if (!isMounted.current) return;
    setIsSaving(true);
    try {
      await props.onSave({
        name: effectiveName!,
      });
    } catch {
      if (isMounted.current) setIsSaving(false);
      return;
    }
    if (isMounted.current) {
      setNewName(undefined);
      setNewForward(undefined);
      setIsOpen(false);
      setIsSaving(false);
    }
  };

  const onCancel = () => {
    if (isSaving) {
      return;
    }
    setNewName(undefined);
    setNewForward(undefined);
    setIsOpen(false);
  };

  const modalFooter = [
    <Button key="cancel" disabled={isSaving} onClick={onCancel}>
      {t("cancel")}
    </Button>,
    <Button
      key="save"
      disabled={isSaving || !effectiveName}
      loading={isSaving}
      onClick={onSave}
      type="primary"
    >
      {t("save")}
    </Button>,
  ];

  return (
    <>
      <Button onClick={() => setIsOpen(true)} type="primary">
        {props.modalButtonIcon}
        {props.title}
      </Button>
      <Modal
        title={props.title}
        open={isOpen}
        onCancel={onCancel}
        onOk={onSave}
        footer={modalFooter}
      >
        <div className="vertical-form row-gap">
          <span className="label">{t("organization:name")}</span>
          <Input
            value={effectiveName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("organization:name")}
          />
        </div>
      </Modal>
    </>
  );
}
